import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { JobShift } from "../types";
import CircularProgress from "@mui/material/CircularProgress";

import Chip from "@mui/material/Chip";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as SidebarDashboardIcon } from '../assets/icons/sidebar_dashboard.svg';
import { ReactComponent as SidebarHomeIcon } from '../assets/icons/sidebar_home.svg';
import { jobShiftApi, systemSettingsApi, dashboardApi } from "../services/api";
import api from "../services/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Customized } from 'recharts';
import { useAuth } from "../auth/AuthContext";
import { FeeComparisonResponse } from '../types';
import LeftAccentCard from '../components/LeftAccentCard';
import CrewRankSummary from '../components/CrewRankSummary';




const ZebraBackground = ({ offset }: any) => {
  const x = offset?.left ?? 0;
  const y = offset?.top ?? 0;
  const w = offset?.width ?? 0;
  const h = offset?.height ?? 0;
  const bands = 5;
  const bandH = h / bands;
  return (
    <g>
      {Array.from({ length: bands }).map((_, i) => (
        <rect key={i} x={x} y={y + i * bandH} width={w} height={bandH}
          fill={i % 2 === 0 ? '#f5f5f5' : '#ffffff'} />
      ))}
    </g>
  );
};

const Home: React.FC = () => {
  const [activeShifts, setActiveShifts] = useState<JobShift[]>([]);
  const [filteredShifts, setFilteredShifts] = useState<JobShift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAvailableOnly, setShowAvailableOnly] = useState<boolean>(false);
  const { storeId, checkAdmin } = useAuth();
  const navigate = useNavigate();

  // システム設定の状態
  const [contactUrl, setContactUrl] = useState<string>("/contact");
  const [systemSettingsLoading, setSystemSettingsLoading] = useState<boolean>(true);

  // 承認待ち勤怠の数
  const [pendingApplicantsCount, setPendingApplicantsCount] = useState<number>(0);

  // データセクション用の状態
  const [dashboardData, setDashboardData] = useState<FeeComparisonResponse | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
    // 承認待ちリストを取得
    useEffect(() => {
      const fetchPendingApprovals = async () => {
        if (!storeId) {
          return;
        }

        try {
          const response = await api.attendance.getApprovalList(storeId);
          if (response && response.pending_count !== undefined) {
            setPendingApplicantsCount(response.pending_count);
          }
        } catch (err) {
          console.error('承認待ちリストの取得に失敗しました:', err);
          // エラー時は 0 を設定（セクションを非表示）
          setPendingApplicantsCount(0);
        }
      };

      fetchPendingApprovals();
    }, [storeId]);
  // システム設定取得
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        setSystemSettingsLoading(true);
        // api.tsのsystemSettingsApiを使用
        const settings = await systemSettingsApi.getManagementSettings();

        // CONTACT_URLを探して設定
        const contactSetting = settings.find(setting => setting.setting_key === 'CONTACT_URL');
        if (contactSetting) {
          setContactUrl(contactSetting.setting_value);
        }

        setSystemSettingsLoading(false);
      } catch (err) {
        // エラーが発生した場合はデフォルトの問い合わせURLを使用
        setContactUrl("/contact");
        setSystemSettingsLoading(false);
      }
    };

    fetchSystemSettings();
  }, []);

  // データセクション用のデータ取得
  useEffect(() => {
    const fetchDataStats = async () => {
      if (!storeId) {
        setDataLoading(false);
        return;
      }
      try {
        setDataLoading(true);
        const data = await dashboardApi.getFeeComparison(storeId);
        setDashboardData(data);
        setDataLoading(false);
      } catch (err) {
        console.error('データ統計の取得に失敗しました:', err);
        setDataLoading(false);
      }
    };
    fetchDataStats();
  }, [storeId]);

  // 求人シフト取得
  useEffect(() => {
    const fetchActiveShifts = async () => {
      if (!storeId) {
        setError("店舗IDが利用できません");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // 日本時間の今日の日付をYYYY-MM-DD形式で取得
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0'); // 月は0から始まるので+1する
        const day = String(now.getDate()).padStart(2, '0');
        const today = `${year}-${month}-${day}`;
        
        const response = await jobShiftApi.getByStoreId(storeId, {
          is_active: true,
          from_date: today, // 今日以降の日付でフィルタリング
        });
        setActiveShifts(response.shifts);
        setFilteredShifts(response.shifts);
        setLoading(false);
      } catch (err) {
        setError("求人シフトの取得に失敗しました");
        setLoading(false);
      }
    };

    fetchActiveShifts();
  }, [storeId]);

  // フィルタリングが変更されたときに実行
  useEffect(() => {
    if (showAvailableOnly) {
      const available = activeShifts.filter(
        (shift) => shift.current_applicants < shift.required_workers
      );
      setFilteredShifts(available);
    } else {
      setFilteredShifts(activeShifts);
    }
  }, [showAvailableOnly, activeShifts]);

  // 画面表示時に運営店舗チェック
  useEffect(() => {
    if (storeId) {
      checkAdmin(storeId);
    }
  }, [storeId, checkAdmin]);

  // 承認画面へ移動
  const handleNavigateToApproval = () => {
    navigate('/attendance/approval');
  };



  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      {/* 未承認アラート */}
      {pendingApplicantsCount > 0 && (
        <Box sx={{ position: 'relative', mb: 3, mt: 1 }}>
          {/* 件数バッジ（カード右上に重ねて表示） */}
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              right: -8,
              minWidth: 26,
              height: 26,
              borderRadius: '50%',
              bgcolor: '#e53935',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              zIndex: 1,
              px: 0.5,
            }}
          >
            {pendingApplicantsCount}
          </Box>

          {/* アラートカード：左側赤アクセントボーダー */}
          <LeftAccentCard
            accentColor="#e53935"
            borderColor="#ffd0cc"
          >
            {/* ALERTチップ + テキスト */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip
                label="ALERT"
                size="small"
                sx={{
                  bgcolor: '#e53935',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.68rem',
                  height: 20,
                  borderRadius: '10px',
                  '& .MuiChip-label': { px: '12px' },
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                未承認の方がいらっしゃいます
              </Typography>
            </Box>

            {/* グラデーションボタン */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleNavigateToApproval}
              disableElevation
              sx={{
                background: 'linear-gradient(90deg, #fb923c 0%, #f43f5e 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                py: 0.8,
                borderRadius: '12px',
                letterSpacing: '0.04em',
                '&:hover': {
                  background: 'linear-gradient(90deg, #f97316 0%, #e11d48 100%)',
                },
              }}
            >
              承認を開始
            </Button>
          </LeftAccentCard>
        </Box>
      )}

      {/* ホームセクション（クルー管理サマリを内包。見出しはページタイトルの「ホーム」） */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SvgIcon component={SidebarHomeIcon} inheritViewBox sx={{ fontSize: '1.5rem', mr: 1, color: '#3c3c3c' }} />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#3c3c3c' }}>
            ホーム
          </Typography>
        </Box>
        <CrewRankSummary />
      </Box>

      {/* データセクション */}
      <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SvgIcon component={SidebarDashboardIcon} inheritViewBox sx={{ fontSize: '1.5rem', mr: 1 }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#3c3c3c' }}>
              データ
            </Typography>
          </Box>

          {dataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : dashboardData ? (
            <>
              {/* Re:crewで削減できた金額 */}
              {(() => {
                const { monthly_data, commission_rate } = dashboardData;
                const totalOtherService = monthly_data.reduce((sum, item) => sum + item.commission_fee, 0);
                // 課題6: Re:crew累計利用料金は実請求（FIXED_FEE + ②振込手数料）の月別 fixed_fee 合算。
                const totalRecrew = monthly_data.reduce((sum, item) => sum + item.fixed_fee, 0);
                const totalDifference = totalOtherService - totalRecrew;
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                      Re:crewで削減できた金額
                    </Typography>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, py: 2.5, px: 2, textAlign: 'center', mb: 1.5, bgcolor: '#fff' }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}>
                        {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalDifference)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        合計お得額
                      </Typography>
                    </Box>
                    <Grid container spacing={1.5} alignItems="stretch">
                      <Grid item xs={6} sx={{ display: 'flex' }}>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#fff' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                            {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalRecrew)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', fontSize: '0.68rem' }}>
                            Re:crewの累計利用料金
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sx={{ display: 'flex' }}>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#fff' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                            {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(totalOtherService)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', fontSize: '0.68rem' }}>
                            従来のスキマバイト手数料
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                      ※手数料率{Math.round(commission_rate * 100)}%で算出
                    </Typography>
                  </Box>
                );
              })()}

              {/* 社員登用 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                  社員登用
                </Typography>
                <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    社員登用データは今後連携予定です
                  </Typography>
                </Box>
              </Box>

              {/* 募集回数の推移 */}
              {(() => {
                const currentYear = new Date().getFullYear();
                const recData = Array.from({ length: 12 }, (_, idx) => {
                  const month = idx + 1;
                  const found = dashboardData.monthly_data.find((item) => {
                    const match = typeof item.month === 'string' && item.month.match(/^([0-9]{4})年([0-9]{1,2})月$/);
                    if (!match) return false;
                    return Number(match[1]) === currentYear && Number(match[2]) === month;
                  });
                  return { month, count: found ? found.recruitments : 0, monthLabel: `${month}` };
                });
                const now = new Date();
                const thisMonthRec = recData[now.getMonth()]?.count || 0;
                const lastMonthRec = now.getMonth() > 0 ? recData[now.getMonth() - 1]?.count || 0 : 0;
                const yearTotalRec = recData.reduce((sum, item) => sum + item.count, 0);
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                      募集回数の推移
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      {[
                        { label: '今月', value: thisMonthRec },
                        { label: '先月', value: lastMonthRec },
                        { label: `${currentYear}年度`, value: yearTotalRec },
                      ].map((item) => (
                        <Grid item xs={4} key={item.label}>
                          <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                            <Typography fontWeight="bold" color="text.tertiary" sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                              {item.value}<Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', ml: '2px' }}>名</Typography>
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ width: '100%', height: 220, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={recData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                          <Customized component={ZebraBackground} />
                          <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} label={{ value: `${currentYear}年(月)`, position: 'insideBottom', offset: -10, fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={20} />
                          <Tooltip formatter={(value: number) => [`${value}名`, '募集回数']} labelFormatter={(label) => `${label}月`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4 }} />
                          <Bar dataKey="count" fill="#1976d2" barSize={10} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                );
              })()}

              {/* 応募数の推移 */}
              {(() => {
                const currentYear = new Date().getFullYear();
                const appData = Array.from({ length: 12 }, (_, idx) => {
                  const month = idx + 1;
                  const found = dashboardData.monthly_data.find((item) => {
                    const match = typeof item.month === 'string' && item.month.match(/^([0-9]{4})年([0-9]{1,2})月$/);
                    if (!match) return false;
                    return Number(match[1]) === currentYear && Number(match[2]) === month;
                  });
                  return { month, count: found ? found.applications : 0, monthLabel: `${month}` };
                });
                const now = new Date();
                const thisMonthApp = appData[now.getMonth()]?.count || 0;
                const lastMonthApp = now.getMonth() > 0 ? appData[now.getMonth() - 1]?.count || 0 : 0;
                const yearTotalApp = appData.reduce((sum, item) => sum + item.count, 0);
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                      応募数の推移
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      {[
                        { label: '今月', value: thisMonthApp },
                        { label: '先月', value: lastMonthApp },
                        { label: `${currentYear}年度`, value: yearTotalApp },
                      ].map((item) => (
                        <Grid item xs={4} key={item.label}>
                          <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                            <Typography fontWeight="bold" color="text.tertiary" sx={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                              {item.value}<Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', ml: '2px' }}>名</Typography>
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ width: '100%', height: 220, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={appData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                          <Customized component={ZebraBackground} />
                          <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} label={{ value: `${currentYear}年(月)`, position: 'insideBottom', offset: -10, fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={20} />
                          <Tooltip formatter={(value: number) => [`${value}名`, '応募数']} labelFormatter={(label) => `${label}月`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4 }} />
                          <Bar dataKey="count" fill="#1976d2" barSize={10} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                );
              })()}
            </>
          ) : null}
      </Box>

      </Box>
    </Box>
  );
};

export default Home;